import { AfterViewInit, ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { GridActions, GridService } from '@core/services';
import { remToPx } from '@util/layout';

import { ConsigneeLookupResponseRow } from '#types/consignee';
import { Filters } from '#types/grid';

import { AuthService } from '@auth/services';
import { CONSTANTS } from '@consignee/constants';
import { ConsigneeService } from '@consignee/services';
import { ShipmentService } from '@shipment/services';

@Component({
  selector: 'z-consignee-lookup-dialog',
  templateUrl: './consignee-lookup-dialog.component.html',
  styleUrls: ['./consignee-lookup-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsigneeLookupDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  stateOrProvinceList$ = this.shipmentService.stateOrProvinceDropdownList$;

  form: FormGroup;

  consignee: string;
  selectedRow: ConsigneeLookupResponseRow;

  matchTypes: Record<string, string> = {};

  billTo: string;
  shipTo: string;

  container: HTMLDivElement;
  containerParent: HTMLDivElement;
  containerFilters: HTMLDivElement;
  containerResizeObserver: ResizeObserver;

  lookupId = 'consignee-lookup-dialog';

  gridId = CONSTANTS.GRID_CONSIGNEE_LOOKUP_DIALOG;

  constructor(
    private store: Store,
    public consigneeService: ConsigneeService,
    public shipmentService: ShipmentService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private gridService: GridService,
    private authService: AuthService,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    this.authService.authenticated$.pipe(takeUntil(this.destroy$)).subscribe((authenticated) => {
      if (!authenticated) {
        this.onCancelClick();
      }
    });

    this.consigneeService.consigneeLookupDialogBillTo$
      .pipe(withLatestFrom(this.consigneeService.consigneeLookupDialogShipTo$), take(1))
      .subscribe(([billTo, shipTo]) => {
        this.billTo = billTo;
        this.shipTo = shipTo;
      });

    this.form = new FormGroup({
      consignee: new FormControl(),
      name: new FormControl(),
      stateOrProvince: new FormControl(),
      activeStatusOnly: new FormControl('Y'),
      billTo: new FormControl(this.billTo),
      shipTo: new FormControl(this.shipTo),
    });

    // initial request to populate drop downs
    this.shipmentService.requestLists();

    this.gridService
      .selectedRow$<ConsigneeLookupResponseRow>(this.gridId)
      .pipe(
        filter((x) => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe((selectedRow) => {
        this.selectedRow = selectedRow.data;
        this.consignee = this.selectedRow.consignee;

        if (selectedRow.selectionType === 'double') {
          this.onSelectClick();
        }
      });

    // close dialog if no longer authenticated
    this.authService.authenticated$
      .pipe(
        filter((authenticated) => !authenticated),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.onCancelClick();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();

    this.destroyResizeObserver();
  }

  ngAfterViewInit() {
    this.initResizeObserver();
  }

  initResizeObserver() {
    this.containerResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        const header: HTMLDivElement = this.containerParent.querySelector('.p-dialog-header');
        const filters: HTMLDivElement = this.container.querySelector('.filters');
        const footer: HTMLDivElement = this.container.querySelector('.p-dialog-footer');
        const paginator: HTMLDivElement = this.container.querySelector('.p-paginator');

        let offset = 0;

        if (header) {
          offset += Math.floor(header.offsetHeight);
        }

        if (filters) {
          offset += Math.floor(filters.offsetHeight);
        }

        if (footer) {
          offset += Math.floor(footer.offsetHeight);
        }

        if (paginator) {
          offset += Math.floor(paginator.offsetHeight);
        }

        this.consigneeService.consigneeLookupDialogDimensions$.next({
          container: {
            width: Math.floor(this.containerParent.offsetWidth),
            height: Math.floor(this.containerParent.offsetHeight),
          },
          grid: {
            width: Math.floor(this.containerParent.offsetWidth) - remToPx('2rem'),
            height: Math.floor(this.containerParent.offsetHeight) - offset,
          },
        });
      });
    });

    try {
      this.container = document.querySelector(`.${this.lookupId}`);

      if (this.container) {
        this.containerParent = this.container.closest(`.lookup-dialog`);

        if (this.containerParent) {
          this.containerResizeObserver.observe(this.containerParent);
        }

        this.containerFilters = this.container.querySelector('.filters');

        if (this.containerFilters) {
          this.containerResizeObserver.observe(this.containerFilters);
        }
      }
    } catch {}
  }

  destroyResizeObserver() {
    try {
      if (this.containerResizeObserver && this.containerParent) {
        this.containerResizeObserver.unobserve(this.containerParent);
      }

      if (this.containerResizeObserver && this.containerFilters) {
        this.containerResizeObserver.unobserve(this.containerFilters);
      }
    } catch {}
  }

  onSelectClick() {
    this.ref.close({ consignee: this.consignee, selectedRow: this.selectedRow });
  }

  onCancelClick() {
    this.ref.close(undefined);
  }

  onMatchTypeChange(field: string, matchType: string) {
    this.matchTypes[field] = matchType;
  }

  applyFilters() {
    const filters: Filters = {};

    if (this.form.get('consignee').value) {
      filters.consignee = { value: this.form.get('consignee').value, matchMode: this.matchTypes.consignee ?? 'equals' };
    }

    if (this.form.get('name').value) {
      filters.name = { value: this.form.get('name').value, matchMode: this.matchTypes.name ?? 'startsWith' };
    }

    if (this.form.get('stateOrProvince').value) {
      filters.stateOrProvince = { value: this.form.get('stateOrProvince').value };
    }

    if (this.form.get('activeStatusOnly').value) {
      filters.activeStatusOnly = { value: this.form.get('activeStatusOnly').value };
    }

    if (this.form.get('billTo').value) {
      filters.billTo = { value: this.form.get('billTo').value };
    }

    if (this.form.get('shipTo').value) {
      filters.shipTo = { value: this.form.get('shipTo').value };
    }

    this.store.dispatch(GridActions.SetFilters({ id: this.gridId, filters }));
  }

  reset(): void {
    this.form.setValue({
      consignee: null,
      name: null,
      stateOrProvince: null,
      activeStatusOnly: 'Y',
      billTo: this.billTo,
      shipTo: this.shipTo,
    });
  }

  isInvalid(formControlName: string) {
    return !this.form.controls[formControlName].pristine && this.form.controls[formControlName].invalid;
  }
}
